import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import AccordionBox from "components/Layouts/AccordionBox";
import ColourConstants from "helpers/colourConstants";
import { AddNoteDialog, NoteRow } from "components/Modules/CommonNotes";
import {
	addModelNotes,
	deleteModelNotes,
	getModelNotes,
	updateModelNote,
} from "services/models/modelDetails/details";
import DeleteDialog from "pages/Models/ModelDetails/ModelDetail/DeleteDialog";
import { useDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import NoteContentPopup from "components/Elements/NoteContentPopup";

const useStyles = makeStyles()((theme) => ({
	noteContainer: {
		marginTop: 25,
		display: "flex",
		justifyContent: "flex-end",
	},
	expandIcon: {
		transform: "scale(0.8)",
	},
	sectionHeading: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "17px",
	},
	noteAccordion: {
		borderColor: ColourConstants.commonBorder,
		borderStyle: "solid",
		borderWidth: 1,
		width: "100%",
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
	},

	deleteIcon: {
		transform: "scale(0.7)",
		color: ColourConstants.deleteButton,
		"&:hover": {
			cursor: "pointer",
		},
	},
	view: {
		textDecoration: "underline",
		color: "#307ad7",
		cursor: "pointer",
	},

	actionButton: { padding: "0px 13px 12px 6px" },
}));

const Notes = ({ data, modelId, isReadOnly }) => {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const [modal, setModal] = useState({
		openAddModal: false,
		openDeleteModal: false,
		openContentModal: false,
	});
	const [notes, setNotes] = useState([]);
	const [editData, setEditData] = useState(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const dispatch = useDispatch();

	const handleAddNote = async (note) => {
		const response = await addModelNotes({ modelID: modelId, note: note });

		if (response.status) {
			fetchNotes();
			return { success: true };
		} else {
			dispatch(showError(response?.data?.error || t("message.couldNotAddNote")));
			return { success: false };
		}
	};

	const handleUpdateNote = async (noteId, note) => {
		const response = await updateModelNote(noteId, note);

		if (response.status) {
			fetchNotes();
			return { success: true };
		} else {
			dispatch(
				showError(response?.data?.error || t("message.couldNotUpdateNote"))
			);
			return { success: false };
		}
	};

	const handleEditNote = (noteData) => {
		setEditData(noteData);
		setModal((prev) => ({ ...prev, openAddModal: true }));
	};

	const handleDelete = async () => {
		const {
			openDeleteModal: { id },
		} = modal;
		setIsDeleting(true);
		const response = await deleteModelNotes(id);

		if (response.status) {
			setNotes([...notes.filter((note) => note.id !== id)]);
		} else {
			dispatch(
				showError(response?.data?.error || t("message.couldNotDeleteNote"))
			);
		}

		setIsDeleting(false);
		onCloseDeleteDialog();
	};

	const fetchNotes = async () => {
		const response = await getModelNotes(modelId);

		if (response.status) {
			setNotes(response.data);
		} else {
			dispatch(showError(t("message.failedToLoadNote")));
		}
	};

	const onOpenDeleteDialog = (id) =>
		setModal({ ...modal, openDeleteModal: { id, open: true } });

	const onCloseDeleteDialog = () =>
		setModal({ ...modal, openDeleteModal: false });

	const onOpenAddDialog = () =>
		setModal((prev) => ({ ...prev, openAddModal: true }));

	const onCloseAddDialog = () => {
		setEditData(null);
		setModal((prev) => ({ ...prev, openAddModal: false }));
	};

	const onCloseContentDialog = () =>
		setModal({ ...modal, openContentModal: false });
	const onOpenContentDialog = (note) =>
		setModal({ ...modal, openContentModal: { open: true, note } });

	useEffect(() => {
		if (data && data.length) {
			setNotes(data);
		}
	}, [data]);
	return (
		<div className={classes.noteContainer}>
			<NoteContentPopup
				open={modal.openContentModal.open ?? false}
				onClose={onCloseContentDialog}
				note={modal.openContentModal.note}
			/>
			<AddNoteDialog
				open={modal.openAddModal}
				handleClose={onCloseAddDialog}
				createHandler={handleAddNote}
				updateHandler={handleUpdateNote}
				editData={editData}
			/>
			<DeleteDialog
				entityName="Note"
				open={modal?.openDeleteModal?.open ?? false}
				closeHandler={onCloseDeleteDialog}
				handleDelete={handleDelete}
				isDeleting={isDeleting}
			/>

			<AccordionBox
				title={t("title.notesCount", { count: notes.length })}
				isActionsPresent={isReadOnly ? false : true}
				buttonName={t("action.addNote")}
				buttonAction={onOpenAddDialog}
				accordianDetailsCss="table-container"
			>
				<Table>
					<TableHead className={classes.tableHead}>
						<TableRow>
							<TableCell style={{ width: "170px" }}>
								{t("label.name")}
							</TableCell>
							<TableCell>{t("label.date")}</TableCell>
							<TableCell>{t("label.note")}</TableCell>
							<TableCell></TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{notes?.map((row) => (
							<NoteRow
								key={row.id}
								row={row}
								classes={classes}
								onDeleteNote={onOpenDeleteDialog}
								onEditNote={handleEditNote}
								onViewNote={onOpenContentDialog}
								isReadOnly={isReadOnly}
							/>
						))}
					</TableBody>
				</Table>
			</AccordionBox>
		</div>
	);
};

export default Notes;
